import { Fragment, useState, useCallback } from 'react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import {
  XMarkIcon,
  PhotoIcon,
  LinkIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import { useDropzone } from 'react-dropzone';
import { apiHelpers } from '../../services/api';
import toast from 'react-hot-toast';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB

export default function AddImageBlockModal({ isOpen, onClose, dashboardId, onImageCreated }) {
  const [activeTab, setActiveTab] = useState(0);
  const [imageUrl, setImageUrl] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [altText, setAltText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetState = () => {
    setActiveTab(0);
    setImageUrl('');
    setFile(null);
    setPreview(null);
    setTitle('');
    setAltText('');
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetState();
    onClose();
  };

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      const err = rejectedFiles[0].errors[0];
      if (err.code === 'file-too-large') {
        toast.error('File is too large. Maximum size is 2MB.');
      } else if (err.code === 'file-invalid-type') {
        toast.error('Invalid file type. Please upload an image.');
      } else {
        toast.error(err.message);
      }
      return;
    }

    if (acceptedFiles.length > 0) {
      const f = acceptedFiles[0];
      setFile(f);
      setPreview(URL.createObjectURL(f));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.svg'] },
    maxSize: MAX_FILE_SIZE,
    multiple: false
  });

  const clearFile = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
  };

  const handleSubmit = async () => {
    if (activeTab === 0 && !imageUrl.trim()) {
      toast.error('Please enter an image URL');
      return;
    }
    if (activeTab === 1 && !file) {
      toast.error('Please select an image to upload');
      return;
    }

    setIsSubmitting(true);

    try {
      let response;

      if (activeTab === 1 && file) {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('dashboardId', dashboardId);
        if (title.trim()) formData.append('title', title.trim());
        if (altText.trim()) formData.append('altText', altText.trim());
        response = await apiHelpers.createImageBlock(formData);
      } else {
        response = await apiHelpers.createImageBlock({
          dashboardId,
          imageUrl: imageUrl.trim(),
          title: title.trim() || undefined,
          altText: altText.trim() || undefined
        });
      }

      if (response.data?.imageBlock) {
        onImageCreated?.(response.data.imageBlock, response.data.layoutItem);
        toast.success('Image added to dashboard!');
        resetState();
        onClose();
      }
    } catch (error) {
      console.error('Create image block error:', error);
      const msg = error.response?.data?.error || error.message || 'Failed to add image';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const urlPreviewValid = imageUrl.trim() && /^https?:\/\/.+/i.test(imageUrl.trim());

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform bg-white dark:bg-surface-900 rounded-2xl shadow-2xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 dark:border-surface-700">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <PhotoIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-surface-900 dark:text-white">
                        Add Image
                      </Dialog.Title>
                      <p className="text-sm text-surface-500">Add an image from a URL or upload your own</p>
                    </div>
                  </div>
                  <button onClick={handleClose} disabled={isSubmitting} className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 disabled:opacity-50">
                    <XMarkIcon className="w-5 h-5 text-surface-500" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
                  <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
                    <Tab.List className="flex rounded-xl bg-surface-100 dark:bg-surface-800 p-1">
                      <Tab className={({ selected }) =>
                        `w-full rounded-lg py-2 text-sm font-medium leading-5 transition-colors ${
                          selected
                            ? 'bg-white dark:bg-surface-700 text-primary-600 dark:text-primary-400 shadow'
                            : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                        }`
                      }>
                        <LinkIcon className="w-4 h-4 inline mr-1.5" />
                        From URL
                      </Tab>
                      <Tab className={({ selected }) =>
                        `w-full rounded-lg py-2 text-sm font-medium leading-5 transition-colors ${
                          selected
                            ? 'bg-white dark:bg-surface-700 text-primary-600 dark:text-primary-400 shadow'
                            : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300'
                        }`
                      }>
                        <ArrowUpTrayIcon className="w-4 h-4 inline mr-1.5" />
                        Upload
                      </Tab>
                    </Tab.List>

                    <Tab.Panels className="mt-4">
                      {/* URL Tab */}
                      <Tab.Panel className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                            Image URL
                          </label>
                          <input
                            type="url"
                            value={imageUrl}
                            onChange={(e) => setImageUrl(e.target.value)}
                            disabled={isSubmitting}
                            className="w-full px-4 py-2.5 rounded-xl border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white placeholder-surface-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                            placeholder="https://example.com/image.png"
                          />
                        </div>
                        {urlPreviewValid && (
                          <div className="rounded-xl border border-surface-200 dark:border-surface-700 overflow-hidden bg-surface-50 dark:bg-surface-800 p-2">
                            <img
                              src={imageUrl.trim()}
                              alt="Preview"
                              className="max-h-48 mx-auto object-contain rounded-lg"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        )}
                      </Tab.Panel>

                      {/* Upload Tab */}
                      <Tab.Panel className="space-y-4">
                        {!file ? (
                          <div
                            {...getRootProps()}
                            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                              isDragActive
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : 'border-surface-300 dark:border-surface-600 hover:border-primary-400 hover:bg-surface-50 dark:hover:bg-surface-800'
                            }`}
                          >
                            <input {...getInputProps()} />
                            <ArrowUpTrayIcon className="w-10 h-10 mx-auto text-surface-400 mb-3" />
                            <p className="text-sm font-medium text-surface-700 dark:text-surface-300">
                              {isDragActive ? 'Drop image here' : 'Drag & drop an image, or click to browse'}
                            </p>
                            <p className="text-xs text-surface-400 mt-1">
                              JPEG, PNG, GIF, WebP, or SVG. Max 2MB.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div className="rounded-xl border border-surface-200 dark:border-surface-700 overflow-hidden bg-surface-50 dark:bg-surface-800 p-2">
                              <img
                                src={preview}
                                alt="Upload preview"
                                className="max-h-48 mx-auto object-contain rounded-lg"
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-surface-600 dark:text-surface-400 truncate max-w-[280px]">
                                {file.name} ({(file.size / 1024).toFixed(0)} KB)
                              </span>
                              <button
                                onClick={clearFile}
                                disabled={isSubmitting}
                                className="text-sm text-red-500 hover:text-red-600 disabled:opacity-50"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        )}
                      </Tab.Panel>
                    </Tab.Panels>
                  </Tab.Group>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                      Title <span className="text-surface-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      disabled={isSubmitting}
                      className="w-full px-4 py-2.5 rounded-xl border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white placeholder-surface-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                      placeholder="Give this image a title..."
                    />
                  </div>

                  {/* Alt text */}
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1.5">
                      Alt Text <span className="text-surface-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={altText}
                      onChange={(e) => setAltText(e.target.value)}
                      disabled={isSubmitting}
                      className="w-full px-4 py-2.5 rounded-xl border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-white placeholder-surface-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                      placeholder="Describe this image..."
                    />
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end px-6 py-4 border-t border-surface-200 dark:border-surface-700 gap-3">
                  <button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-surface-700 dark:text-surface-300 bg-surface-100 dark:bg-surface-800 rounded-lg hover:bg-surface-200 dark:hover:bg-surface-700 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || (activeTab === 0 && !imageUrl.trim()) || (activeTab === 1 && !file)}
                    className="btn-primary disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <PhotoIcon className="w-4 h-4 mr-2" />
                        Add Image
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
